import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  HardHat, LogOut, Plus, MessageSquare, User, Bell,
  ChevronRight, Clock, MapPin, DollarSign, CheckCircle,
  FileText, AlertCircle, Eye, ThumbsUp, ThumbsDown
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const budgetLabels = {
  under_500: 'Under $500',
  '500_2k': '$500 - $2,000',
  '2k_5k': '$2,000 - $5,000',
  '5k_10k': '$5,000 - $10,000',
  over_10k: 'Over $10,000'
};

const timelineLabels = {
  asap: 'ASAP',
  within_week: 'Within a week',
  within_month: 'Within a month',
  flexible: 'Flexible'
};

const ClientDashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/clients/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectContractor = async (jobId, contractorId) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/select/${contractorId}`);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to select contractor:', error);
      alert(error.response?.data?.detail || 'Failed to select contractor');
    }
  };

  const handleApproveContact = async (requestId) => {
    try {
      const response = await axios.post(`${API}/contact-request/${requestId}/approve`);
      alert(`Contact revealed! ${response.data.client_info?.email}`);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to approve contact:', error);
      alert(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectContact = async (requestId) => {
    try {
      await axios.post(`${API}/contact-request/${requestId}/reject`);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to reject contact:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const { profile, jobs, contact_requests, stats } = dashboardData || {};

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight hidden sm:block">CONTRACTORSLAB</span>
            </Link>
            
            <div className="flex items-center gap-3 md:gap-4">
              <Link to="/tips" className="text-slate-300 hover:text-white text-sm hidden md:flex items-center gap-1.5" data-testid="dashboard-tips-link">
                {t('nav.tips')}
              </Link>
              <LanguageSwitcher variant="dark" />
              <button className="text-slate-400 hover:text-white relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                {contact_requests?.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-600 rounded-full text-xs text-white flex items-center justify-center">
                    {contact_requests.length}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-slate-300" />
                </div>
                <span className="text-white text-sm hidden md:block">{user?.first_name}</span>
              </div>
              <button 
                onClick={handleLogout}
                className="text-slate-400 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Profile Setup Alert */}
        {!profile && (
          <div className="bg-orange-50 border border-orange-200 rounded-sm p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <span className="text-orange-800">Add your address to get better contractor matches</span>
            </div>
            <Link 
              to="/client/profile"
              className="bg-orange-600 text-white px-4 py-2 rounded-sm text-sm font-bold"
            >
              Complete Profile
            </Link>
          </div>
        )}

        {/* Pending Contact Requests Alert */}
        {contact_requests?.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-sm p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800 font-bold">You have {contact_requests.length} contact request(s) to review</span>
            </div>
            <div className="space-y-2">
              {contact_requests.map((request) => (
                <div key={request.id} className="bg-white rounded p-3 flex items-center justify-between">
                  <span className="text-sm text-slate-700">Contractor wants to share contact info</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleApproveContact(request.id)}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm font-bold flex items-center gap-1"
                      data-testid={`approve-contact-${request.id}`}
                    >
                      <ThumbsUp className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={() => handleRejectContact(request.id)}
                      className="bg-slate-200 text-slate-700 px-3 py-1 rounded text-sm font-bold flex items-center gap-1"
                      data-testid={`reject-contact-${request.id}`}
                    >
                      <ThumbsDown className="w-4 h-4" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500 uppercase font-bold">{t('dashboard.totalJobs')}</span>
              <FileText className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-3xl font-black text-slate-900" data-testid="total-jobs">
              {stats?.total_jobs || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500 uppercase font-bold">{t('dashboard.activeJobs')}</span>
              <Clock className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-3xl font-black text-slate-900" data-testid="active-jobs">
              {stats?.active_jobs || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500 uppercase font-bold">{t('dashboard.completedJobs')}</span>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-black text-slate-900" data-testid="completed-jobs">
              {stats?.completed_jobs || 0}
            </p>
          </div>
          
          <Link
            to="/client/post-job"
            className="bg-orange-600 hover:bg-orange-700 rounded-sm p-6 flex flex-col items-center justify-center transition-colors"
            data-testid="post-job-btn"
          >
            <Plus className="w-8 h-8 text-white mb-2" />
            <span className="text-white font-bold uppercase">{t('nav.postJob')}</span>
          </Link>
        </div>

        {/* Jobs List */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-sm border border-slate-200">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-bold text-slate-900">My Jobs</h3>
                <Link 
                  to="/client/post-job"
                  className="text-orange-600 text-sm font-bold flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Post Job
                </Link>
              </div>
              <div className="divide-y divide-slate-100">
                {jobs?.length > 0 ? (
                  jobs.map((job) => (
                    <div 
                      key={job.id} 
                      className={`p-4 cursor-pointer transition-colors ${selectedJob?.id === job.id ? 'bg-orange-50' : 'hover:bg-slate-50'}`}
                      onClick={() => setSelectedJob(selectedJob?.id === job.id ? null : job)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-slate-900">{job.title}</h4>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                              job.status === 'active' ? 'bg-green-100 text-green-700' :
                              job.status === 'contractor_selected' ? 'bg-blue-100 text-blue-700' :
                              job.status === 'matching' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {job.status.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {job.zip_code}
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" /> {budgetLabels[job.budget_range]}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {timelineLabels[job.timeline]}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-bold">
                            {job.proposals_count || 0} proposals
                          </span>
                          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${selectedJob?.id === job.id ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                      
                      {/* Expanded Proposals */}
                      {selectedJob?.id === job.id && job.proposals?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <h5 className="font-bold text-sm text-slate-700 mb-3">Proposals ({job.proposals.length})</h5>
                          <div className="space-y-3">
                            {job.proposals.map((proposal) => (
                              <div 
                                key={proposal.id} 
                                className={`p-4 rounded border ${
                                  proposal.status === 'selected' ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-slate-50'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-slate-900">
                                        {proposal.contractor?.business_name || 'Contractor'}
                                      </span>
                                      {proposal.contractor?.license_verified === 'approved' && (
                                        <span className="text-xs text-green-600 font-bold">Licensed</span>
                                      )}
                                    </div>
                                    <p className="text-sm text-slate-600 mt-1">{proposal.intro_message}</p>
                                    <div className="flex items-center gap-4 mt-2 text-sm">
                                      <span className="text-slate-700 font-medium">
                                        ${proposal.estimated_price_min} - ${proposal.estimated_price_max}
                                      </span>
                                      <span className="text-slate-500">
                                        {proposal.estimated_timeline_days} days
                                      </span>
                                    </div>
                                  </div>
                                  {!job.selected_contractor_id && proposal.status === 'pending' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelectContractor(job.id, proposal.contractor_id);
                                      }}
                                      className="bg-orange-600 text-white px-4 py-2 rounded-sm text-sm font-bold"
                                      data-testid={`select-contractor-${proposal.id}`}
                                    >
                                      Select
                                    </button>
                                  )}
                                  {proposal.status === 'selected' && (
                                    <div className="flex items-center gap-2">
                                      <Link
                                        to={`/client/messages/${job.id}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-bold flex items-center gap-1"
                                        data-testid={`message-contractor-${proposal.id}`}
                                      >
                                        <MessageSquare className="w-4 h-4" /> Message
                                      </Link>
                                      {job.contact_revealed && (
                                        <Link
                                          to={`/client/review/${job.id}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className="bg-orange-600 text-white px-3 py-1 rounded text-sm font-bold"
                                          data-testid={`review-job-${job.id}`}
                                        >
                                          Review
                                        </Link>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No jobs posted yet</p>
                    <Link
                      to="/client/post-job"
                      className="inline-flex items-center gap-2 text-orange-600 font-bold mt-2"
                    >
                      <Plus className="w-4 h-4" /> Post your first job
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-sm border border-slate-200 p-4">
              <h3 className="font-bold text-slate-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link
                  to="/client/post-job"
                  className="flex items-center justify-between p-3 bg-orange-50 rounded hover:bg-orange-100 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-orange-700">
                    <Plus className="w-4 h-4" /> Post a New Job
                  </span>
                  <ChevronRight className="w-4 h-4 text-orange-400" />
                </Link>
                <Link
                  to="/client/messages"
                  className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <MessageSquare className="w-4 h-4" /> Messages
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </Link>
                <Link
                  to="/client/financing"
                  className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <DollarSign className="w-4 h-4" /> Apply for Financing
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </Link>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-slate-900 rounded-sm p-6">
              <h3 className="font-bold text-white mb-3">Pro Tips</h3>
              <ul className="space-y-3 text-sm text-slate-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                  Review all proposals before selecting
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                  Message contractors to discuss details
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                  Only approve contact when ready to hire
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
